import { Dispatch, useState } from 'react';

import {
  Calendar,
  ChakraAvatar,
  ChakraBox,
  ChakraFlex,
  ChakraGridItem,
  ChakraSimpleGrid,
  ChakraText,
  EquipUIFireflyV2Theme,
  EquipUITheme,
  PinArea,
  Popover,
  useMediaQuery,
} from '@equip.health/ui';
import { DateTime } from 'luxon';
import { useFlags } from 'launchdarkly-react-client-sdk';

import { popoverBodyContent, popoverTriggerContent } from './PatientHeader';
import IconText from '~/components/common/IconText';
import ActionsCta from '~/components/maud/patients/profile/patient-header/ActionsCta';
import { PatientHeaderAvatarFallBackIcon } from '~/components/maud/patients/profile/patient-header/PatientHeaderAvatarFallBackIcon';
import { PatientHeaderSuicideRiskTag } from '~/components/maud/patients/profile/patient-header/PatientHeaderSuicideRiskTag';
import { PatientStatusHeaderLegacy } from '~/components/maud/patients/profile/patient-header/PatientStatusHeaderLegacy';
import { PatientStatusHeader } from '~/components/maud/patients/profile/patient-header/PatientStatusHeader';

import { AssessmentAction } from '~/lib/constants/patients.constants';
import { SurveyName } from '~/lib/constants/survey/SurveyName';
import useGetPatientStatus from '~/lib/hooks/useGetPatientStatus';
import usePatientAlerts from '~/lib/hooks/usePatientAlerts';

import {
  getExactAgeFromDOB,
  getPreferredFullName,
} from '~/lib/util/patient.util';
import { onRecordNewSurvey } from '~/lib/util/surveyActions.util';
import { useGetPatientClinicalProfileQuery } from '~/store/services/clinicalData';
import PatientAlerts from '~/components/maud/patients/profile/patient-header/PatientAlerts';

const { h4 } = EquipUITheme.typography;
const { secondary } = EquipUIFireflyV2Theme.colors;

const TOP_LEVEL_ID = 'patient-header';

interface PatientHeaderProps {
  onContinueSurvey: (externalId: string) => void;
  patient: Patient;
  patientId: string;
  patientPreferredFullName: string;
  updateAssessmentState: Dispatch<AssessmentAction>;
}

const PatientHeader = ({
  onContinueSurvey,
  patient,
  patientId,
  patientPreferredFullName,
  updateAssessmentState,
}: PatientHeaderProps) => {
  const {
    displayInsuranceAuthEndingDates: isDisplayInsuranceAuthEndingDatesEnabled,
    enableDisplayInsurancePart1: isEnableDisplayInsurancePart1,
  } = useFlags<LDFlags>();

  const showNewPatientStatusHeader =
    isDisplayInsuranceAuthEndingDatesEnabled || isEnableDisplayInsurancePart1;

  const {
    avatarSrc,
    chosenName,
    dateOfBirth,
    firstName,
    genderOffRecord,
    genderOnRecord,
    lastName,
    pronounsOffRecord,
    pronounsOnRecord,
    state,
  } = patient ?? {};

  const patientAlerts = usePatientAlerts(
    patientId,
    patientPreferredFullName,
    genderOnRecord,
  );

  const { data: patientClinicalProfile } = useGetPatientClinicalProfileQuery(
    patientId,
    {
      skip: !patientId,
    },
  );

  const { status, style } = useGetPatientStatus({ patient });

  const [isGenderPronounPopoverOpen, setIsGenderPronounPopoverOpen] =
    useState(false);

  const [wrapHeaderContent] = useMediaQuery('(max-width: 1190px)');

  if (!patientClinicalProfile) {
    // declining to show spinner in the header since there should be one in the body of the page already
    // box with height prevents page layout from jostling around
    return <ChakraBox height="119px" />;
  }

  const patientFullName = getPreferredFullName(chosenName, firstName, lastName);

  return (
    <ChakraFlex
      alignContent="center"
      alignItems="center"
      aria-label="Patient header"
      flexWrap={wrapHeaderContent ? 'wrap' : 'nowrap'}
      gridGap={2}
      id={TOP_LEVEL_ID}
      justifyContent="space-between"
      padding="24.5px 0px 12.5px 0px"
      width="100%"
    >
      <ChakraSimpleGrid
        templateColumns="repeat(11, 1fr)"
        templateRows="repeat(2, 1fr)"
      >
        <ChakraGridItem colSpan={1} marginY="auto" rowSpan={2}>
          <ChakraAvatar
            background="tertiary.marigold.background"
            color="primary.90.variant"
            height="56px"
            icon={
              <PatientHeaderAvatarFallBackIcon
                patientFullName={patientFullName}
              />
            }
            id="patient-header__avatar-image"
            overflow="hidden"
            src={avatarSrc}
            width="56px"
          />
        </ChakraGridItem>
        <ChakraGridItem colSpan={10} paddingLeft="8px" rowSpan={1}>
          <ChakraFlex alignItems="center" id={`${TOP_LEVEL_ID}__patient-name`}>
            <ChakraText
              {...h4}
              as="span"
              paddingRight="8px"
              textTransform="capitalize"
            >
              {patientFullName}
            </ChakraText>

            <PatientHeaderSuicideRiskTag
              genderOnRecord={genderOnRecord}
              id={TOP_LEVEL_ID}
              patientId={patientId}
            />
          </ChakraFlex>
        </ChakraGridItem>
        <ChakraGridItem
          colSpan={10}
          justifyItems="center"
          marginY="auto"
          paddingLeft="8px"
          rowSpan={1}
        >
          <ChakraFlex alignItems="center">
            <ChakraBox>
              <Popover
                bodyContent={popoverBodyContent({
                  genderOffRecord,
                  genderOnRecord,
                  pronounsOffRecord,
                  pronounsOnRecord,
                })}
                id={`${TOP_LEVEL_ID}__gender-pronouns-popover`}
                isOpen={isGenderPronounPopoverOpen}
                margin="0px 0px 0px 90px"
                maxHeight="auto"
                onClose={() => setIsGenderPronounPopoverOpen(false)}
                onOpen={() => {
                  setIsGenderPronounPopoverOpen(true);
                }}
                placement="bottom-start"
                size="sm"
                trigger="hover"
                triggerContent={popoverTriggerContent({
                  genderOffRecord,
                  genderOnRecord,
                })}
              />
            </ChakraBox>
            <ChakraBox id={`${TOP_LEVEL_ID}__state`} paddingLeft="16px">
              <IconText
                content={[state]}
                icon={
                  <PinArea
                    fill={secondary[50]}
                    fillOpacity={0.5}
                    height="20px"
                    width="20px"
                  />
                }
              />
            </ChakraBox>
            <ChakraBox id={`${TOP_LEVEL_ID}__date-of-birth`} paddingLeft="16px">
              <IconText
                content={
                  dateOfBirth
                    ? [
                        `${DateTime.fromSQL(dateOfBirth).toFormat(
                          'LL/dd/yyyy',
                        )} (${getExactAgeFromDOB(dateOfBirth, 'yyyy-LL-dd')})`,
                      ]
                    : ['unknown']
                }
                icon={
                  <Calendar fill={secondary[50]} height="20px" width="20px" />
                }
              />
            </ChakraBox>
          </ChakraFlex>
        </ChakraGridItem>
      </ChakraSimpleGrid>
      <ChakraFlex alignItems="center" gap="12px">
        {patientId && (
          <>
            {showNewPatientStatusHeader ? (
              <PatientStatusHeader
                patientId={patientId}
                patientStatus={status}
                patientStatusCode={patient?.universalStatusCode}
                patientStatusColor={style?.tagColor}
                primaryDiagnosis={patientClinicalProfile?.primaryDiagnosis}
              />
            ) : (
              <PatientStatusHeaderLegacy
                patientId={patientId}
                patientStatus={status}
                patientStatusCode={patient?.universalStatusCode}
                patientStatusColor={style?.tagColor}
                primaryDiagnosis={patientClinicalProfile?.primaryDiagnosis}
              />
            )}
          </>
        )}
        <PatientAlerts
          patientAlerts={patientAlerts}
          onAddNewSurvey={(surveyName: SurveyName) => {
            onRecordNewSurvey(surveyName, updateAssessmentState);
          }}
          onContinueSurvey={onContinueSurvey}
          patientId={patientId}
        />
        <ActionsCta
          patient={patient}
          patientClinicalProfile={patientClinicalProfile}
          patientPreferredFullName={patientPreferredFullName}
          updateAssessmentState={updateAssessmentState}
        />
      </ChakraFlex>
    </ChakraFlex>
  );
};

export default PatientHeader;
